import { HotKeyBox } from '@modules/common/components/HotKeyIcons/HotKeyBox';
import { useHotkey } from '@modules/common/hooks';
import { IntegrationSource } from '@modules/integrationsV2/types';
import { trackIntegrationPageManageMetadataEvent } from '@modules/integrationsV2/utils';
import { ArrowBackRounded, ArrowForwardRounded } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';

interface ManageMetadataEditDrawerHeaderProps {
  onNext?: () => void;
  onPrevious?: () => void;
  currentFieldIndex: number;
  totalFieldCount: number;
  integrationSource: IntegrationSource;
}

export const ManageMetadataEditDrawerHeader = ({
  onNext,
  onPrevious,
  currentFieldIndex,
  totalFieldCount,
  integrationSource,
}: ManageMetadataEditDrawerHeaderProps) => {
  const handleNext = () => {
    onNext?.();
    trackIntegrationPageManageMetadataEvent({
      eventName: 'Manage Metadata Drawer - Next Page Button Clicked',
      integration: {
        channelType: integrationSource.channel.type,
        integrationType: integrationSource.integrationType,
        namespace: integrationSource.namespace,
      },
    });
  };

  const handlePrevious = () => {
    onPrevious?.();
    trackIntegrationPageManageMetadataEvent({
      eventName: 'Manage Metadata Drawer - Previous Page Button Clicked',
      integration: {
        channelType: integrationSource.channel.type,
        integrationType: integrationSource.integrationType,
        namespace: integrationSource.namespace,
      },
    });
  };

  useHotkey({
    hotkey: 'ArrowRight',
    onHotkey: () => onNext?.(),
    actionTitle: 'Go to next metadata',
  });

  useHotkey({
    hotkey: 'ArrowLeft',
    onHotkey: () => onPrevious?.(),
    actionTitle: 'Go to previous metadata',
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      px={3}
      py={2}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'misc.drawer.background',
        position: 'sticky',
        top: 0,
        width: '100%',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Stack>
        <Typography variant="h6" color="text.secondary">
          Edit Metadata
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Button
          variant="outlined"
          startIcon={
            <HotKeyBox
              hotkeyDisplay={<ArrowBackRounded fontSize="small" />}
              isDisabled={!onPrevious}
            />
          }
          disabled={!onPrevious}
          onClick={handlePrevious}
        >
          Previous
        </Button>
        <Typography whiteSpace="nowrap">
          {currentFieldIndex + 1} of {totalFieldCount}
        </Typography>
        <Button
          variant="outlined"
          endIcon={
            <HotKeyBox
              hotkeyDisplay={<ArrowForwardRounded fontSize="small" />}
              isDisabled={!onNext}
            />
          }
          disabled={!onNext}
          onClick={handleNext}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
};
